import { useQuery } from "@tanstack/react-query";

import { api } from "../../lib/apiClient";

/**
 * Kayıt ekranındaki hukuki onay kutuları (CLOS-342).
 *
 * **Her belge AYRI kutudur.** Tek bir "hepsini kabul ediyorum" kutusu, kullanıcının
 * pazarlama iznini fark etmeden vermesine yol açardı — oysa kart açık: pazarlama izni
 * zorunlu onaylardan ayrı tutulur ve verilmemesi kaydı bloke etmez.
 *
 * Metinler ve sürümler SUNUCUDAN gelir; istemci sabit bir sürüm bilmez. Bilseydi, metin
 * güncellendiğinde eski sürümü onaylatmaya devam ederdi.
 */
export type LegalDocument = {
  id: string;
  type: string;
  version: number;
  locale: string;
  required: boolean;
};

/** Kullanıcıya görünen adlar. Tür kapalı küme olduğu için burada da kapalı. */
const BASLIKLAR: Record<string, string> = {
  terms_of_service: "Kullanım Şartları",
  privacy_policy: "Gizlilik Politikası",
  marketing: "Kampanya ve duyuru e-postaları almak istiyorum",
};

function baslik(type: string): string {
  return BASLIKLAR[type] ?? type;
}

export function useLegalDocuments() {
  return useQuery({
    queryKey: ["session", "legal-documents"],
    queryFn: async () => (await api<LegalDocument[]>("/api/session/legal-documents")).data,
  });
}

/**
 * Kullanıcının onaylaması GEREKEN ama onaylamadığı belgeler (CLOS-343).
 *
 * Hem kapı (`RequireCurrentConsents`) hem de yeniden onay ekranı AYNI sorguyu kullanır;
 * ikisi farklı kaynaklardan beslenseydi kapı "eksik var" derken ekran boş kalabilirdi.
 */
export function usePendingConsents() {
  return useQuery({
    queryKey: ["session", "pending-consents"],
    queryFn: async () => (await api<LegalDocument[]>("/api/session/pending-consents")).data,
  });
}

export function LegalConsentFields({
  belgeler,
  secilenler,
  degistir,
  hata,
}: {
  belgeler: LegalDocument[];
  secilenler: string[];
  degistir: (id: string, secili: boolean) => void;
  hata?: string;
}) {
  if (belgeler.length === 0) return null;

  return (
    <fieldset className="flex flex-col gap-2">
      {/* Grup etiketi: ekran okuyucu kutuların neye ait olduğunu duyurabilsin. */}
      <legend className="text-sm font-medium text-slate-700">Onaylar</legend>

      {belgeler.map((belge) => (
        <label key={belge.id} className="flex items-start gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            className="mt-1"
            checked={secilenler.includes(belge.id)}
            onChange={(e) => degistir(belge.id, e.target.checked)}
          />
          <span>
            {belge.required ? (
              <>
                {/* Bağlantı AYRI bir odak hedefi: kullanıcı kutuyu işaretlemeden önce
                    metni okuyabilmeli ve klavyeyle oraya ulaşabilmeli. */}
                <a
                  href={`/legal/${belge.type}?v=${belge.version}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary hover:underline"
                >
                  {baslik(belge.type)}
                </a>
                {"'nı okudum ve kabul ediyorum"}
                <span aria-hidden="true"> *</span>
                <span className="sr-only"> (zorunlu)</span>
              </>
            ) : (
              baslik(belge.type)
            )}
          </span>
        </label>
      ))}

      {hata ? (
        <p role="alert" className="text-sm text-danger">
          {hata}
        </p>
      ) : null}
    </fieldset>
  );
}
